import { ArrayElement, Course } from "utils/interfaces";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import MyLearningJourneyElement from "./MyLearningJourneyElement";

interface MyLearningJourneyPanelProps {
  ref?: any;
  course: Course;
}

type CombinedItem = {
  item: ArrayElement<Course["projects"]> | ArrayElement<Course["quizzes"]>;
  type: "project" | "quiz";
  originalIndex: number;
};

const MyLearningJourneyPanel: React.FC<MyLearningJourneyPanelProps> =
  forwardRef(({ course }, ref) => {
    // States definition
    const [uncompletedIndexes, setUncompletedIndexes] = useState<number[]>([]);
    const [hereIndex, setHereIndex] = useState<number | null>(null);

    // Define a reference to each project badge inside this panel
    const projectsRef = useRef<{ [key: number]: any }>({});

    const combinedItems = useMemo<CombinedItem[]>(() => {
      const items: CombinedItem[] = [];

      if (course.projects && course.projects.length > 0) {
        course.projects.forEach((project, index) => {
          items.push({
            item: project,
            type: "project",
            originalIndex: index,
          });
        });
      }

      if (course.quizzes && course.quizzes.length > 0) {
        course.quizzes.forEach((quiz, index) => {
          items.push({
            item: quiz,
            type: "quiz",
            originalIndex: index,
          });
        });
      }
      return items;
    }, [course.projects, course.quizzes]);

    /**
     * Functions and elements to expose to parents components
     */
    useImperativeHandle(ref, () => ({
      pressCurrentProject() {
        pressCurrentProject();
      },
    }));

    /**
     * Function to define the indexes of the lessons that are uncompleted
     * on this panel
     */
    useEffect(() => {
      // Define the current index
      if (uncompletedIndexes.length > 0) {
        setHereIndex(Math.min(...uncompletedIndexes));
      } else {
        setHereIndex(null);
      }
    }, [uncompletedIndexes]);

    /**
     * Function used to pass to the parent element to allow
     * clicking on the current lesson
     */
    const pressCurrentProject = () => {
      // Get the current project ref
      const currentProjectRef = projectsRef.current[hereIndex || 0];

      // Click that project
      currentProjectRef.badge.handleClickBadge();
    };

    return (
      <div className="grow-0 shrink-0 w-full flex flex-col gap-2">
        <div className="self-stretch text-neutral-500 text-xs font-normal font-sans leading-[14px] select-none">
          {course.title || "..."}
        </div>
        <div className="w-full h-full pt-4 custom-scroll [--scroll-color:theme(colors.neutral.25)] overflow-x-hidden overflow-y-auto z-10">
          {/*overflow-y-auto*/}
          <div
            data-journey-panel
            className={
              "w-full h-full justify-items-center grid grid-cols-3 gap-6"
            }
          >
            {combinedItems.length > 0 ? (
              combinedItems.map(({ item, type }, index) => (
                <MyLearningJourneyElement
                  key={`${type}-${index}`}
                  ref={(element: any) => {
                    projectsRef.current[index] = element;
                  }}
                  index={index}
                  project={item as any}
                  toComplete={index === hereIndex}
                  setUncompletedIndexes={setUncompletedIndexes}
                  itemType={type}
                />
              ))
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  });

export default MyLearningJourneyPanel;
